import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import Logo from '../images/school_logo.png';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import { db } from '../config/fbConfig';
import { collection, getDocs, query, where, doc, getDoc, writeBatch } from 'firebase/firestore';

const SubjectScores = () => {
  const { classId, subjectId } = useParams();
  const [searchParams] = useSearchParams();
  
  const [students, setStudents] = useState([]);
  const [classInfo, setClassInfo] = useState(null);
  const [subjectInfo, setSubjectInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editableScores, setEditableScores] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [saving, setSaving] = useState(false);

  const getGrade = (total) => {
    if (total >= 70) return 'A';
    if (total >= 60) return 'B';
    if (total >= 50) return 'C';
    if (total >= 45) return 'D';
    if (total >= 40) return 'E';
    return 'F';
  };

  const validateScore = (value, field) => {
    const numValue = Number(value);
    if (isNaN(numValue)) return false;
    
    if (field === 'exam') {
      return numValue >= 0 && numValue <= 60;
    } else { // For CA fields
      return numValue >= 0 && numValue <= 20;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const classDoc = await getDoc(doc(db, 'classes', classId));
        setClassInfo(classDoc.data());
  
        const subjectDoc = await getDoc(doc(db, 'subjects', subjectId));
        setSubjectInfo(subjectDoc.data());
  
        const studentIds = classDoc.data().students || [];
  
        let studentsSnapshotDocs = [];
        for (let i = 0; i < studentIds.length; i += 30) {
          const chunk = studentIds.slice(i, i + 30);
          const studentsQuery = query(
            collection(db, 'students'),
            where('__name__', 'in', chunk)
          );
          const chunkSnapshot = await getDocs(studentsQuery);
          studentsSnapshotDocs.push(...chunkSnapshot.docs);
        }
  
        const initialEditableScores = {};
        const studentsData = studentsSnapshotDocs.map(doc => {
          const studentData = doc.data();
          const enrollment = studentData.enrollments?.find(e => e.classId === classId);
          const subject = enrollment?.subjects?.find(s => s.subjectId === subjectId);
  
          const firstCA = subject?.scores?.firstca ?? 0;
          const secondCA = subject?.scores?.secondca ?? 0;
          const exam = subject?.scores?.exam ?? 0;
  
          const total = firstCA + secondCA + exam;
          const grade = getGrade(total);
  
          // Store initial editable scores
          initialEditableScores[doc.id] = {
            firstCA: firstCA === 0 ? '' : firstCA,
            secondCA: secondCA === 0 ? '' : secondCA,
            exam: exam === 0 ? '' : exam
          };
  
          return {
            id: doc.id,
            ...studentData,
            scores: {
              firstCA,
              secondCA,
              exam
            },
            total,
            grade
          };
        });
  
        setEditableScores(initialEditableScores);
        studentsData.sort((a, b) => b.total - a.total);
        const studentsWithPosition = studentsData.map((student, index) => ({
          ...student,
          position: index + 1
        }));
  
        setStudents(studentsWithPosition);
      } catch (error) {
        setError("Error fetching data: " + error.message);
        setShowModal(true);
      } finally {
        setLoading(false);
      }
    };
  
    fetchData();
  }, [classId, subjectId]);  

  const handleScoreChange = (studentId, field, value) => {
    // Handle empty string (when clearing the input)
    if (value === '') {
      setEditableScores(prev => ({
        ...prev,
        [studentId]: {
          ...prev[studentId],
          [field]: ''
        }
      }));
      setIsDirty(true);
      return;
    }

    // Validate the input
    if (!validateScore(value, field)) return;

    // Handle numeric input
    const numValue = Number(value);
    if (isNaN(numValue)) return;

    setEditableScores(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: numValue
      }
    }));
    setIsDirty(true);
  };

  const saveAllScores = async () => {
    if (!isDirty) return;
  
    setSaving(true); // Start saving
    try {
      const batch = writeBatch(db);
  
      students.forEach(student => {
        const studentRef = doc(db, 'students', student.id);
        const enrollments = student.enrollments || [];
        const updatedEnrollments = enrollments.map(enrollment => {
          if (enrollment.classId === classId) {
            const updatedSubjects = enrollment.subjects?.map(subject => {
              if (subject.subjectId === subjectId) {
                return {
                  ...subject,
                  scores: {
                    ...(subject.scores || {}),
                    firstca: editableScores[student.id]?.firstCA || 0,
                    secondca: editableScores[student.id]?.secondCA || 0,
                    exam: editableScores[student.id]?.exam || 0
                  }
                };
              }
              return subject;
            });
            return {
              ...enrollment,
              subjects: updatedSubjects
            };
          }
          return enrollment;
        });
        batch.update(studentRef, { enrollments: updatedEnrollments });
      });
  
      await batch.commit();
  
      const updatedStudents = students.map(student => {
        const scores = editableScores[student.id] || {};
        const firstCA = scores.firstCA || 0;
        const secondCA = scores.secondCA || 0;
        const exam = scores.exam || 0;
        const total = firstCA + secondCA + exam;
        const grade = getGrade(total);
        return {
          ...student,
          scores: { firstCA, secondCA, exam },
          total,
          grade
        };
      }).sort((a, b) => b.total - a.total).map((student, index) => ({
        ...student,
        position: index + 1
      }));
  
      setStudents(updatedStudents);
      setIsDirty(false);
      setSuccessMessage("Scores saved successfully!");
      setShowModal(true);
    } catch (error) {
      setError("Failed to save scores. Please try again.");
      setShowModal(true);
    } finally {
      setSaving(false); // Stop saving
    }
  };
  

  if (loading) {
    return <Loader text={"LOADING"} />;
  }

  return (
    <>
      <div className="scroll-fixed" style={{minHeight: '100vh'}}>
        <div className="top-elements row align-center space-between">
          <Link to="/home" className="back-link">
            <i className="fa-solid fa-arrow-left size3"></i>
          </Link>
          <div className="column align-center justify-center">
            <img src={Logo} className="middle-logo" alt="logo" />
          </div>
          <Link to="#" className="home-link" onClick={() => window.location.reload()}>
            <i className="fa-solid fa-rotate-right size4"></i>
          </Link>
        </div>
        <div style={{ maxWidth: '100vw', padding: '20px', marginTop: '80px' }}>
          <p className="caps male-female-stat">Student Scores for {subjectInfo?.name}</p>
          <p className="bold male-female-stat">{classInfo?.name} ({searchParams.get('sessionName')} - {searchParams.get('termName')} Term)</p>      
          
          <div className="scroll-fixed scroll-fixed1" style={{ overflowX: 'auto', width: '100%' }}>
            <table style={{ 
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.9rem'
            }}
            className='male-female-stat'
            >
              <thead>
                <tr style={{ backgroundColor: '#f2f2f2' }}>
                  <th style={{ padding: '4px', border: '1px solid #ddd', textAlign: 'center', width: '40px' }}>S/N</th>
                  <th style={{ padding: '4px', border: '1px solid #ddd', textAlign: 'left', minWidth: '120px' }}>Student</th>
                  <th style={{ padding: '4px', border: '1px solid #ddd', textAlign: 'center', width: '60px' }}>1st CA</th>
                  <th style={{ padding: '4px', border: '1px solid #ddd', textAlign: 'center', width: '60px' }}>2nd CA</th>
                  <th style={{ padding: '4px', border: '1px solid #ddd', textAlign: 'center', width: '60px' }}>Exam</th>
                  <th style={{ padding: '4px', border: '1px solid #ddd', textAlign: 'center', width: '60px' }}>Total</th>
                  <th style={{ padding: '4px', border: '1px solid #ddd', textAlign: 'center', width: '60px' }}>Grade</th>
                </tr>
              </thead>
              <tbody>
                {students.map(student => {
                  const scores = editableScores[student.id] || {};
                  const firstCA = scores.firstCA || 0;
                  const secondCA = scores.secondCA || 0;
                  const exam = scores.exam || 0;
                  const total = firstCA + secondCA + exam;
                  const grade = getGrade(total);
                  
                  return (
                    <tr className='male-female-stat' key={student.id} style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '4px', textAlign: 'center' }}>{student.position}</td>
                      <td style={{ padding: '4px' }}>{student.firstName} {student.lastName}</td>
                      <td style={{ padding: '4px', textAlign: 'center' }}>
                        <input
                          className="score-input male-female-stat"
                          type="number"
                          min="0"
                          max="20"
                          value={scores.firstCA ?? ''}
                          onChange={(e) => handleScoreChange(student.id, 'firstCA', e.target.value)}
                          style={{ width: '50px', height: '40px', textAlign: 'center', padding: '2px' }}
                        />
                      </td>
                      <td style={{ padding: '4px', textAlign: 'center' }}>
                        <input
                          className="score-input male-female-stat"
                          type="number"
                          min="0"
                          max="20"
                          value={scores.secondCA ?? ''}
                          onChange={(e) => handleScoreChange(student.id, 'secondCA', e.target.value)}                          
                          style={{ width: '50px', height: '40px', textAlign: 'center', padding: '2px' }}
                        />
                      </td>
                      <td style={{ padding: '4px', textAlign: 'center' }}>
                        <input
                          className="score-input male-female-stat"
                          type="number"
                          min="0"
                          max="60"
                          value={scores.exam ?? ''}
                          onChange={(e) => handleScoreChange(student.id, 'exam', e.target.value)}
                          style={{ width: '50px', height: '40px', textAlign: 'center', padding: '2px' }}
                        />
                      </td>
                      <td style={{ padding: '4px', height: '40px', textAlign: 'center', fontWeight: 'bold' }}>
                        {total}
                      </td>
                      <td style={{ 
                        padding: '4px', 
                        textAlign: 'center', 
                        fontWeight: 'bold',
                        color: grade === 'F' ? 'red' : 'inherit'
                      }}>
                        {grade}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>                  
        </div>
        <div style={{ marginTop: '-10px', marginLeft: '20px', textAlign: 'left' }}>
            <button
              onClick={saveAllScores}
              disabled={saving}
              className='male-female-stat'
              style={{
                padding: '8px 16px',
                backgroundColor: isDirty ? 'rgb(224, 193, 107)' : '#cccccc',
                color: isDirty ? '#111d2b' : '#111d2bad',
                border: 'none',
                borderRadius: '4px',
                cursor: isDirty ? 'pointer' : 'not-allowed'
              }}
            >
              {saving ? 'Saving Scores...' : 'Save All Scores'}
            </button>
            {isDirty && <span className="size5 male-female-stat" style={{ marginLeft: '10px', color: '#666' }}>You have unsaved changes</span>}
          </div>
      </div>

      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}
    </>
  );
};

export default SubjectScores;